import { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import Icon from './Icons';
import SpeciesCareProfile from './SpeciesCareProfile';
import { fetchExplore, fetchExploreDetail } from './api';
import { SkeletonCard, SkeletonLine } from './Skeleton';
import { photoUrl } from './photoUtils';

const DIFFICULTY_STYLE = {
  beginner:     { background: 'var(--green-light, #f0f7f1)', color: 'var(--green, #3a7d44)', border: '1px solid var(--green-border, #c3dfc9)' },
  intermediate: { background: '#fff8ec', color: '#b87400', border: '1px solid #f0d899' },
  advanced:     { background: '#fff0f0', color: 'var(--red, #e05c5c)', border: '1px solid #f5c6c6' },
};

const GROWTH_HABIT_LABEL = {
  climbing_vine:       'Climbing',
  trailing_vine:       'Trailing',
  terrestrial_crawler: 'Crawler',
  self_heading:        'Self-heading',
  rosette:             'Rosette',
  upright:             'Upright',
};

const PHOTO_PART_LABELS = {
  full_plant: 'Full plant',
  leaf:       'Leaf',
  petiole:    'Petiole',
  stem_node:  'Stem / node',
  roots:      'Roots',
  flower:     'Flower',
  fruit:      'Fruit',
  habit:      'Growth habit',
  other:      'Other',
};

const PHOTO_PART_ORDER = ['full_plant', 'leaf', 'habit', 'stem_node', 'petiole', 'flower', 'fruit', 'roots', 'other', 'unlabeled'];

function photoPartLabel(part) {
  return PHOTO_PART_LABELS[part] || 'Unlabeled';
}

const PHOTO_GROWTH_STAGE_LABELS = {
  new_growth: 'New growth',
  juvenile: 'Juvenile',
  transitional: 'Transitional',
  mature: 'Mature',
};

function photoGrowthStageLabel(stage) {
  return PHOTO_GROWTH_STAGE_LABELS[stage] || null;
}

function textValue(...values) {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (Array.isArray(value) && value.length) return value.filter(Boolean).join(', ');
  }
  return null;
}

function traitItemsFromMap(source, fields) {
  return fields.map(([key, label]) => {
    const value = textValue(source?.[key]);
    return value ? { label, value } : null;
  }).filter(Boolean);
}

export function buildIdentificationTraitGroups(profile) {
  const traits = profile?.identification_traits || {};
  const gh = profile?.growth_habit || {};

  const groups = [
    {
      key: 'leaf',
      label: 'Leaf',
      items: [
        { label: 'Shape', value: textValue(traits.leaf?.shape, traits.leaf_shape, gh.leaf_shape) },
        { label: 'Texture', value: textValue(traits.leaf?.texture, traits.leaf_texture, gh.leaf_texture) },
        { label: 'Venation', value: textValue(traits.leaf?.venation, traits.venation, gh.venation) },
        { label: 'Color / pattern', value: textValue(traits.leaf?.color_pattern, traits.leaf?.variegation, traits.variegation) },
      ].filter(item => item.value),
    },
    {
      key: 'petiole',
      label: 'Petiole',
      items: [
        { label: 'Traits', value: textValue(traits.petiole?.description, traits.petiole, gh.petiole) },
        ...traitItemsFromMap(traits.petiole, [
          ['color', 'Color'],
          ['texture', 'Texture'],
          ['sheath', 'Sheath'],
        ]),
      ],
    },
    {
      key: 'stem_node',
      label: 'Stem / node',
      items: [
        { label: 'Growth form', value: textValue(traits.stem_node?.growth_form, traits.stem?.growth_form, gh.stem_growth_form) },
        ...traitItemsFromMap(traits.stem_node || traits.stem, [
          ['color', 'Color'],
          ['internode_spacing', 'Internodes'],
          ['cataphyll', 'Cataphyll'],
          ['node_notes', 'Node notes'],
        ]),
      ],
    },
    {
      key: 'roots',
      label: 'Roots',
      items: traitItemsFromMap(traits.roots, [
        ['aerial_roots', 'Aerial roots'],
        ['root_color', 'Color'],
        ['notes', 'Notes'],
      ]),
    },
    {
      key: 'growth_form',
      label: 'Growth form',
      items: [
        { label: 'Habit', value: textValue(traits.growth_form?.habit, gh.type?.map(h => GROWTH_HABIT_LABEL[h] || h.replace('_', ' '))) },
        { label: 'Support', value: textValue(traits.growth_form?.support, gh.support?.notes) },
        { label: 'Juvenile vs mature', value: textValue(traits.growth_form?.juvenile_mature, traits.juvenile_mature) },
      ].filter(item => item.value),
    },
    {
      key: 'flowers_fruit',
      label: 'Flower / fruit',
      items: traitItemsFromMap(traits.flowers_fruit, [
        ['inflorescence', 'Inflorescence'],
        ['flower', 'Flower'],
        ['fruit', 'Fruit'],
        ['notes', 'Notes'],
      ]),
    },
  ];

  return groups
    .map(group => ({ ...group, items: group.items.filter(item => item?.value) }))
    .filter(group => group.items.length > 0);
}

export function groupCollectionPhotosByPart(photos = []) {
  const groupsByPart = new Map();
  for (const photo of photos) {
    const part = photo.photo_part || 'unlabeled';
    if (!groupsByPart.has(part)) {
      groupsByPart.set(part, {
        part,
        label: photoPartLabel(photo.photo_part),
        photos: [],
      });
    }
    groupsByPart.get(part).photos.push(photo);
  }

  return [...groupsByPart.values()].sort((a, b) => {
    const aIndex = PHOTO_PART_ORDER.indexOf(a.part);
    const bIndex = PHOTO_PART_ORDER.indexOf(b.part);
    return (aIndex === -1 ? 99 : aIndex) - (bIndex === -1 ? 99 : bIndex);
  });
}

export function isGenusProfile(item) {
  return item?.species === 'sp.' || item?.profile_type === 'genus';
}

export function sortExploreResults(items) {
  return [...(items || [])].sort((a, b) => {
    const genusCompare = String(a.genus || '').localeCompare(String(b.genus || ''));
    if (genusCompare !== 0) return genusCompare;
    if (isGenusProfile(a) !== isGenusProfile(b)) return isGenusProfile(a) ? -1 : 1;
    return String(a.species || '').localeCompare(String(b.species || ''));
  });
}

export function buildGenusGroups(items = []) {
  const groups = [];
  let currentGenus = null;
  let currentItems = [];
  for (const item of items) {
    if (item.genus !== currentGenus) {
      if (currentGenus !== null) groups.push({ genus: currentGenus, items: currentItems });
      currentGenus = item.genus;
      currentItems = [item];
    } else {
      currentItems.push(item);
    }
  }
  if (currentGenus !== null) groups.push({ genus: currentGenus, items: currentItems });
  return groups;
}

function splitTradeNameSpecies(species) {
  const clean = String(species || '').trim();
  const unknownMatch = clean.match(/^(sp|sp\.|spp|spp\.|unknown|unk)\.?\s+(.+)$/i);
  if (unknownMatch) {
    return { species: 'sp.', trade_name: unknownMatch[2].trim() };
  }

  const parts = clean.split(/\s+/).filter(Boolean);
  if (parts.length > 1) {
    return {
      species: parts[0],
      trade_name: parts.slice(1).join(' '),
    };
  }

  return { species: 'sp.', trade_name: clean };
}

export function buildExplorePrefill({ genus, species, profile_type, common_name }) {
  if (profile_type === 'trade_name') {
    const parsedTradeName = splitTradeNameSpecies(species);
    return {
      genus,
      species: parsedTradeName.species,
      trade_name: parsedTradeName.trade_name,
      common_name,
      id_basis: 'trade_name',
      taxonomy_confidence: parsedTradeName.species === 'sp.' ? 'unknown' : 'unresolved',
    };
  }

  return { genus, species, common_name };
}

function ExploreCard({ item }) {
  const navigate = useNavigate();
  const firstCommonName = item.common_names?.[0] || null;
  const otherNames = item.common_names?.slice(1) || [];
  const genusProfile = isGenusProfile(item);
  const displayScientificName = genusProfile ? item.genus : `${item.genus} ${item.species}`;
  const prefill = buildExplorePrefill({
    genus: item.genus,
    species: item.species,
    profile_type: item.profile_type,
    common_name: firstCommonName,
  });

  const handleCardClick = () => {
    navigate(`/explore/${encodeURIComponent(item.genus)}/${encodeURIComponent(item.species)}`);
  };

  const handleAddCollection = (e) => {
    e.stopPropagation();
    navigate('/new/manual', { state: { prefill } });
  };

  const handleAddWishlist = (e) => {
    e.stopPropagation();
    navigate('/new/wishlist', { state: { prefill } });
  };

  return (
    <div className={`explore-card${genusProfile ? ' explore-card--genus-profile' : ''}`} onClick={handleCardClick} role="button" tabIndex={0}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleCardClick(); } }}>
      {/* Photo area */}
      {!genusProfile && (
        <div className="explore-card-photo">
          {item.reference_hero_ref_id ? (
            <>
              <img
                src={`/reference-photos/${item.reference_hero_ref_id}/file${item.reference_hero_transform_version ? `?v=${item.reference_hero_transform_version}` : ''}`}
                alt={firstCommonName || `${item.genus} ${item.species}`}
                className="explore-card-photo-img"
                loading="lazy"
              />
              <span className="explore-card-photo-label">Reference</span>
              {item.reference_hero_growth_stage && (
                <span className="explore-card-photo-stage">{photoGrowthStageLabel(item.reference_hero_growth_stage)}</span>
              )}
            </>
          ) : item.collection_photo_id ? (
            <>
              <img
                src={photoUrl(item.collection_photo_id, item.collection_photo_rotation_version, item.collection_photo_created_at)}
                alt={firstCommonName || `${item.genus} ${item.species}`}
                className="explore-card-photo-img"
                loading="lazy"
              />
              <span className="explore-card-photo-label">From your collection</span>
              {item.collection_photo_part && (
                <span className="explore-card-photo-part">{photoPartLabel(item.collection_photo_part)}</span>
              )}
              {item.collection_photo_growth_stage && (
                <span className="explore-card-photo-stage">{photoGrowthStageLabel(item.collection_photo_growth_stage)}</span>
              )}
            </>
          ) : (
            <div className="explore-card-photo-placeholder">
              <span className="explore-card-photo-initial">{item.genus[0]}</span>
            </div>
          )}
        </div>
      )}

      {/* Card body */}
      <div className="explore-card-body">
        {genusProfile && (
          <div className="explore-card-kicker">
            <Icon name="leaf" size={12} />
            <span>Genus overview</span>
          </div>
        )}
        <h3 className="explore-card-scientific">
          <em>{displayScientificName}</em>
        </h3>
        {genusProfile && (
          <p className="explore-card-context">General care and ID notes for the genus.</p>
        )}
        {firstCommonName && <p className="explore-card-common">{firstCommonName}</p>}
        {otherNames.length > 0 && (
          <p className="explore-card-aliases">{otherNames.join(', ')}</p>
        )}

        {!genusProfile && (
          <>
            <div className="explore-card-chips">
              {item.care_difficulty && (
                <span className="explore-chip" style={DIFFICULTY_STYLE[item.care_difficulty]}>
                  {item.care_difficulty}
                </span>
              )}
              {item.growth_habit?.slice(0, 2).map(h => (
                <span key={h} className="explore-chip explore-chip--habit">
                  {GROWTH_HABIT_LABEL[h] || h.replace('_', ' ')}
                </span>
              ))}
              {item.toxic && (
                <span className="explore-chip explore-chip--toxic">
                  <Icon name="alertTriangle" size={10} />
                  <span>Toxic</span>
                </span>
              )}
            </div>

            {item.in_collection > 0 && (
              <span className="explore-status-badge explore-status-collection">
                In collection ({item.in_collection})
              </span>
            )}
            {item.in_collection === 0 && item.in_wishlist && (
              <span className="explore-status-badge explore-status-wishlist">On wishlist</span>
            )}
          </>
        )}
      </div>

      {/* Action buttons */}
      {!genusProfile && (
        <div className="explore-card-actions">
          <button className="btn btn-primary btn-sm" onClick={handleAddCollection}>
            {item.in_collection > 0 ? 'New Specimen' : 'Add to Collection'}
          </button>
          {!item.in_wishlist && (
            <button className="btn btn-secondary btn-sm" onClick={handleAddWishlist}>
              Add to Wishlist
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default function Explore() {
  const [query, setQuery] = useState('');
  const [debouncedQ, setDebouncedQ] = useState('');
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  const browseMode = !debouncedQ;
  const genusGroups = useMemo(
    () => browseMode ? buildGenusGroups(results) : [],
    [browseMode, results]
  );

  // 300ms debounce
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(query), 300);
    return () => clearTimeout(t);
  }, [query]);

  // Fetch on debounced query change
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchExplore(debouncedQ, 40, 0)
      .then(data => {
        if (cancelled) return;
        setResults(sortExploreResults(data.results));
        setTotal(data.total);
        setLoading(false);
      })
      .catch(err => {
        if (cancelled) return;
        setError(err.message);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [debouncedQ]);

  const handleLoadMore = () => {
    setLoadingMore(true);
    fetchExplore(debouncedQ, 40, results.length)
      .then(data => {
        setResults(r => sortExploreResults([...r, ...data.results]));
        setTotal(data.total);
        setLoadingMore(false);
      })
      .catch(() => setLoadingMore(false));
  };

  const jumpGenus = (direction) => {
    const sections = [...document.querySelectorAll('[data-explore-genus]')];
    if (sections.length <= 1) return;

    const anchorOffset = 118;
    const currentIndex = sections.reduce((best, section, index) => {
      const distance = Math.abs(section.getBoundingClientRect().top - anchorOffset);
      return distance < best.distance ? { index, distance } : best;
    }, { index: 0, distance: Infinity }).index;
    const nextIndex = Math.max(0, Math.min(sections.length - 1, currentIndex + direction));
    const target = sections[nextIndex];
    target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // Build genus-grouped list for browse mode
  const renderResults = () => {
    if (loading) {
      return (
        <div className="explore-grid">
          {[1, 2, 3, 4, 5, 6].map(i => <SkeletonCard key={i} />)}
        </div>
      );
    }

    if (error) {
      return (
        <div className="explore-empty-state">
          <div className="explore-empty-state-icon"><Icon name="alertTriangle" size={32} /></div>
          <p>Failed to load profiles: {error}</p>
        </div>
      );
    }

    if (results.length === 0) {
      return (
        <div className="explore-empty-state">
          <div className="explore-empty-state-icon"><Icon name="search" size={40} /></div>
          <p>{debouncedQ ? `No profiles found for "${debouncedQ}"` : 'No care profiles available yet.'}</p>
        </div>
      );
    }

    if (debouncedQ) {
      // Search mode: flat grid
      return (
        <div className="explore-grid">
          {results.map(item => <ExploreCard key={item.id} item={item} />)}
        </div>
      );
    }

    // Browse mode: grouped by genus
    return genusGroups.map(({ genus, items }) => (
      <div key={genus} className="explore-genus-section" data-explore-genus={genus}>
        <h2 className="explore-genus-heading"><em>{genus}</em></h2>
        <div className="explore-grid">
          {items.map(item => <ExploreCard key={item.id} item={item} />)}
        </div>
      </div>
    ));
  };

  return (
    <div className="app">
      <header className="app-header">
        <div className="app-header-brand">
          <div>
            <h1>Explore</h1>
            {!loading && (
              <p className="plant-count">
                {total === 0 ? 'No profiles' : `${total} ${total === 1 ? 'profile' : 'profiles'}`}
                {debouncedQ ? ` for "${debouncedQ}"` : ''}
              </p>
            )}
          </div>
        </div>
      </header>

      <div className="explore-search-bar">
        <div style={{ position: 'relative' }}>
          <input
            ref={inputRef}
            type="text"
            className="search-input"
            style={{ width: '100%', boxSizing: 'border-box', paddingRight: query ? '2.2rem' : undefined }}
            placeholder="Search by name, genus, or common name…"
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
          {query && (
            <button
              className="explore-search-clear"
              onClick={() => { setQuery(''); inputRef.current?.focus(); }}
              aria-label="Clear search"
              title="Clear search"
            >
              <Icon name="x" size={14} />
            </button>
          )}
        </div>
      </div>

      <div className="explore-content">
        {renderResults()}

        {!loading && results.length < total && (
          <div className="explore-load-more-wrap">
            <button
              className="btn btn-secondary"
              onClick={handleLoadMore}
              disabled={loadingMore}
            >
              {loadingMore ? 'Loading…' : `Load more (${total - results.length} remaining)`}
            </button>
          </div>
        )}
      </div>

      {!loading && !error && browseMode && genusGroups.length > 1 && createPortal(
        <div className="explore-genus-jump-rail" aria-label="Genus navigation">
          <button
            type="button"
            className="explore-genus-jump explore-genus-jump--prev"
            onClick={() => jumpGenus(-1)}
            aria-label="Previous genus"
            title="Previous genus"
          >
            <Icon name="chevronUp" size={18} />
          </button>
          <button
            type="button"
            className="explore-genus-jump explore-genus-jump--next"
            onClick={() => jumpGenus(1)}
            aria-label="Next genus"
            title="Next genus"
          >
            <Icon name="chevronDown" size={18} />
          </button>
        </div>,
        document.body
      )}
    </div>
  );
}

export function ExploreDetail() {
  const { genus, species } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetchExploreDetail(genus, species)
      .then(result => {
        if (!result) { setError('Profile not found'); setLoading(false); return; }
        setData(result);
        setLoading(false);
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, [genus, species]);

  useEffect(() => {
    if (loading) return;
    const savedScroll = sessionStorage.getItem('exploreDetailScroll');
    if (!savedScroll) return;
    requestAnimationFrame(() => window.scrollTo(0, parseInt(savedScroll, 10) || 0));
    sessionStorage.removeItem('exploreDetailScroll');
  }, [loading]);

  if (loading) {
    return (
      <div className="app">
        <header className="sub-header">
          <button className="sub-header-back" onClick={() => navigate('/explore', { replace: true })} aria-label="Back" title="Back">
            <Icon name="arrowLeft" size={20} />
          </button>
          <div className="sub-header-title">
            <h1>Loading…</h1>
          </div>
        </header>
        <div style={{ padding: '1rem' }}>
          <SkeletonLine width="60%" height="1.5rem" style={{ marginBottom: '0.5rem' }} />
          <SkeletonLine width="40%" height="1rem" style={{ marginBottom: '1rem' }} />
          <SkeletonLine width="100%" height="8rem" />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="app">
        <header className="sub-header">
          <button className="sub-header-back" onClick={() => navigate('/explore', { replace: true })} aria-label="Back" title="Back">
            <Icon name="arrowLeft" size={20} />
          </button>
          <div className="sub-header-title">
            <h1>Not found</h1>
          </div>
        </header>
        <div className="explore-empty-state">
          <p>{error || 'Profile not found'}</p>
        </div>
      </div>
    );
  }

  const profile = data.profile_json;
  const firstCommonName = profile?.common_names?.[0] || null;
  const allCommonNames = profile?.common_names || [];
  const prefill = buildExplorePrefill({
    genus,
    species,
    profile_type: data.profile_type,
    common_name: firstCommonName,
  });
  const gh = profile?.growth_habit || {};
  const diagnosticFeatures = gh.diagnostic_features || [];
  const idTraitGroups = buildIdentificationTraitGroups(profile);
  const collectionSpecimens = data.collection_specimens || [];
  const heroSpecimen = collectionSpecimens.find(specimen => specimen.photo_id) || null;

  const careProfileForComponent = { ...data, match_type: 'exact' };
  const goToSpecimen = (plantId) => {
    sessionStorage.setItem('exploreDetailScroll', String(window.scrollY));
    navigate(`/plants/${plantId}`, { state: { fromExplore: location.pathname } });
  };

  return (
    <div className="app">
      <header className="sub-header">
        <button className="sub-header-back" onClick={() => navigate('/explore', { replace: true })} aria-label="Back" title="Back">
          <Icon name="arrowLeft" size={20} />
        </button>
        <div className="sub-header-title">
          <h1>{firstCommonName || `${genus} ${species}`}</h1>
          <p className="sub-header-subtitle"><em>{genus} {species !== 'sp.' ? species : 'sp.'}</em></p>
        </div>
      </header>

      {data.reference_hero_ref_id ? (
        <div className="explore-detail-hero">
          <img
            src={`/reference-photos/${data.reference_hero_ref_id}/file${data.reference_hero_transform_version ? `?v=${data.reference_hero_transform_version}` : ''}`}
            alt={`${genus} ${species}`}
            className="explore-detail-hero-img"
          />
          <span className="explore-detail-hero-label">Reference photo</span>
          {data.reference_hero_growth_stage && (
            <span className="explore-detail-hero-stage">{photoGrowthStageLabel(data.reference_hero_growth_stage)}</span>
          )}
          {data.reference_hero_attribution && (
            <span className="explore-detail-hero-credit">
              {data.reference_hero_source_url
                ? <a href={data.reference_hero_source_url} target="_blank" rel="noopener noreferrer">
                    {data.reference_hero_attribution}
                  </a>
                : data.reference_hero_attribution}
              {data.reference_hero_license && <> · {data.reference_hero_license}</>}
            </span>
          )}
        </div>
      ) : heroSpecimen ? (
        <button
          type="button"
          className="explore-detail-hero"
          onClick={() => goToSpecimen(heroSpecimen.plant_id)}
          aria-label={`Open ${heroSpecimen.specimen_id}`}
        >
          <img
            src={photoUrl(heroSpecimen.photo_id, heroSpecimen.rotation_version, heroSpecimen.created_at)}
            alt={`${photoPartLabel(heroSpecimen.photo_part)} from ${heroSpecimen.specimen_id}`}
            className="explore-detail-hero-img"
          />
          {heroSpecimen.growth_stage && (
            <span className="explore-detail-hero-stage">{photoGrowthStageLabel(heroSpecimen.growth_stage)}</span>
          )}
          <span className="explore-detail-hero-label">
            {photoPartLabel(heroSpecimen.photo_part)}
            <span>{heroSpecimen.specimen_id}</span>
          </span>
        </button>
      ) : null}

      <div className="explore-detail-content">

        {/* -- Collection status badges -- */}
        {(data.in_collection > 0 || data.in_wishlist) && (
          <div style={{ marginBottom: '0.75rem' }}>
            {data.in_collection > 0 && (
              <span className="explore-status-badge explore-status-collection" style={{ marginRight: '0.4rem' }}>
                In collection ({data.in_collection})
              </span>
            )}
            {data.in_collection === 0 && data.in_wishlist && (
              <span className="explore-status-badge explore-status-wishlist">On wishlist</span>
            )}
          </div>
        )}

        {/* -- Action buttons -- */}
        <div className="explore-detail-actions">
          <button
            className="btn btn-primary"
            onClick={() => navigate('/new/manual', { state: { prefill } })}
          >
            {data.in_collection > 0 ? 'New Specimen' : 'Add to Collection'}
          </button>
          {!data.in_wishlist && (
            <button
              className="btn btn-secondary"
              onClick={() => navigate('/new/wishlist', { state: { prefill } })}
            >
              Add to Wishlist
            </button>
          )}
          <button
            className="btn btn-secondary"
            onClick={() => navigate(`/compare?profiles=${data.id}`, { state: { returnTo: location.pathname } })}
          >
            Compare with...
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => {
              const q = encodeURIComponent(`${genus} ${species}`);
              window.open(`https://www.google.com/search?tbm=isch&q=${q}`, '_blank', 'noopener,noreferrer');
            }}
          >
            <Icon name="image" size={14} style={{ marginRight: '0.3rem' }} />
            Look up photos
          </button>
        </div>

        {/* -- Specimens from collection -- */}
        {collectionSpecimens.length > 0 && (
          <div className="explore-detail-section">
            <div className="explore-detail-section-title">From your collection</div>
            <div className="explore-specimen-strip">
              {collectionSpecimens.map(specimen => (
                <button
                  type="button"
                  key={specimen.plant_id}
                  className="explore-specimen-card"
                  onClick={() => goToSpecimen(specimen.plant_id)}
                >
                  <span className="explore-specimen-photo">
                    {specimen.photo_id ? (
                      <img
                        src={photoUrl(specimen.photo_id, specimen.rotation_version, specimen.created_at)}
                        alt={`${specimen.specimen_id}`}
                        loading="lazy"
                      />
                    ) : (
                      <span className="explore-specimen-photo-placeholder">
                        {specimen.specimen_id?.[0] || genus[0]}
                      </span>
                    )}
                    {specimen.photo_part && (
                      <span className="explore-specimen-photo-part">{photoPartLabel(specimen.photo_part)}</span>
                    )}
                    {specimen.growth_stage && (
                      <span className="explore-specimen-photo-stage">{photoGrowthStageLabel(specimen.growth_stage)}</span>
                    )}
                  </span>
                  <span className="explore-specimen-meta">
                    <strong>{specimen.specimen_id}</strong>
                    {specimen.common_name && <span>{specimen.common_name}</span>}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* -- Identification section -- */}
        <div className="explore-id-section">
          <div className="explore-detail-section-title">Identification</div>

          {allCommonNames.length > 0 && (
            <div style={{ marginBottom: '0.75rem' }}>
              <div className="explore-trait-label">Common names</div>
              <div style={{ fontSize: '0.9rem', color: 'var(--text)' }}>{allCommonNames.join(' · ')}</div>
            </div>
          )}

          {profile?.distinguishing_notes && (
            <div style={{ marginBottom: '0.75rem' }}>
              <div className="explore-trait-label">About</div>
              <p style={{ fontSize: '0.875rem', color: 'var(--text)', lineHeight: 1.5, margin: 0 }}>
                {profile.distinguishing_notes}
              </p>
            </div>
          )}

          {diagnosticFeatures.length > 0 && (
            <div style={{ marginBottom: '0.75rem' }}>
              <div className="explore-trait-label">Key features</div>
              <ul className="explore-diagnostic-list">
                {diagnosticFeatures.map((f, i) => <li key={i}>{f}</li>)}
              </ul>
            </div>
          )}

          {idTraitGroups.length > 0 && (
            <div style={{ marginBottom: '0.75rem' }}>
              <div className="explore-trait-label">ID traits</div>
              <div className="explore-id-trait-groups">
                {idTraitGroups.map(group => (
                  <div key={group.key} className="explore-id-trait-group">
                    <div className="explore-id-trait-group-title">{group.label}</div>
                    <dl className="explore-id-trait-list">
                      {group.items.map(({ label, value }) => (
                        <div key={`${group.key}-${label}`} className="explore-id-trait-row">
                          <dt>{label}</dt>
                          <dd>{value}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                ))}
              </div>
            </div>
          )}

          {gh.type?.length > 0 && (
            <div style={{ marginBottom: '0.75rem' }}>
              <div className="explore-trait-label">Growth habit</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
                {gh.type.map(h => (
                  <span key={h} className="explore-chip explore-chip--habit">
                    {GROWTH_HABIT_LABEL[h] || h.replace('_', ' ')}
                  </span>
                ))}
              </div>
            </div>
          )}

          {profile?.toxicity && (
            <div style={{ marginBottom: '0.75rem' }}>
              <div className="explore-trait-label">Toxicity</div>
              {profile.toxicity.toxic ? (
                <span className="explore-chip explore-chip--toxic" style={{ display: 'inline-flex' }}>
                  <Icon name="alertTriangle" size={12} />
                  <span>
                    Toxic
                    {profile.toxicity.severity && ` (${profile.toxicity.severity})`}
                    {profile.toxicity.affected && ` — ${profile.toxicity.affected === 'both' ? 'pets & children' : profile.toxicity.affected}`}
                  </span>
                </span>
              ) : (
                <span style={{ fontSize: '0.83rem', color: 'var(--green, #3a7d44)' }}>Non-toxic</span>
              )}
            </div>
          )}
        </div>

        {/* -- Full care profile -- */}
        <SpeciesCareProfile
          plant={{ genus, species }}
          profile={careProfileForComponent}
          loading={false}
          hideIdentitySections
        />
      </div>
    </div>
  );
}
